/**
 * HubSpotConnection - Connection management card for the HubSpot integration.
 *
 * Displays the current HubSpot connection status and provides controls for
 * connecting (entering a Private App token) or disconnecting. When connected,
 * shows the portal name, who configured the connection, and when.
 *
 * States:
 * - Disconnected: shows a token input form with "Test & Connect" button
 * - Connected: shows portal info with a "Disconnect" button
 * - Loading: spinner while testing connection or fetching status
 *
 * Used inside the Integrations tab on the Admin page.
 */
import React, { useState } from 'react';
import type { HubSpotConnectionStatus } from '../../../services/hubspotService';
import * as hubspotService from '../../../services/hubspotService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

interface HubSpotConnectionProps {
    /** Current connection status — fetched by the parent and passed down */
    status: HubSpotConnectionStatus | null;
    /** Whether the status is still loading */
    loading: boolean;
    /** Callback to refresh the connection status after connect/disconnect */
    onStatusChange: () => void;
    /** Optional extra classes for the outer card */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const HubSpotConnection: React.FC<HubSpotConnectionProps> = ({
    status,
    loading,
    onStatusChange,
    className,
}) => {
    // Token input for connecting to HubSpot
    const [token, setToken] = useState('');
    // Loading state while testing the connection
    const [connecting, setConnecting] = useState(false);
    // Loading state while disconnecting
    const [disconnecting, setDisconnecting] = useState(false);
    // Error message from a failed connection attempt
    const [error, setError] = useState<string | null>(null);
    // Success message after a successful connection
    const [success, setSuccess] = useState<string | null>(null);
    // Whether the user has confirmed they want to disconnect
    const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

    /**
     * Handle the "Test & Connect" button click.
     * Sends the token to the backend which validates it against HubSpot
     * and stores the connection settings if valid.
     */
    const handleConnect = async () => {
        if (!token.trim()) {
            setError('Please enter a Private App access token');
            return;
        }

        setConnecting(true);
        setError(null);
        setSuccess(null);

        try {
            const result = await hubspotService.connect(token.trim());
            setSuccess(`Connected to ${result.portal_name || 'HubSpot'}`);
            setToken('');
            // Notify parent to refresh the connection status
            onStatusChange();
        } catch (err: unknown) {
            // Extract error message from the API error response
            const apiError = err as { data?: { message?: string }; message?: string };
            setError(apiError?.data?.message || apiError?.message || 'Failed to connect to HubSpot');
        } finally {
            setConnecting(false);
        }
    };

    /**
     * Handle the "Disconnect" confirmation.
     * Removes the stored token and portal info from the database.
     */
    const handleDisconnect = async () => {
        setDisconnecting(true);
        setError(null);

        try {
            await hubspotService.disconnect();
            setShowDisconnectConfirm(false);
            setSuccess(null);
            onStatusChange();
        } catch (err: unknown) {
            const apiError = err as { message?: string };
            setError(apiError?.message || 'Failed to disconnect');
        } finally {
            setDisconnecting(false);
        }
    };

    // Show a loading skeleton while fetching the initial status
    if (loading && !status) {
        return (
            <div className={`hubspot-connection bg-white rounded-lg shadow-sm border border-gray-200 p-16px tablet:p-24px ${className ?? ''}`} data-ref="organism-hubspot-connection">
                <div className="animate-pulse">
                    <div className="h-20px bg-gray-200 rounded w-1/3 mb-16px"></div>
                    <div className="h-16px bg-gray-200 rounded w-2/3"></div>
                </div>
            </div>
        );
    }

    return (
        <div className={`hubspot-connection bg-white rounded-lg shadow-sm border border-gray-200 p-16px tablet:p-24px ${className ?? ''}`} data-ref="organism-hubspot-connection">
            {/* Header section with HubSpot branding */}
            <div className="hubspot-connection__header mb-16px">
                {/* Wraps on narrow screens: the badge drops below the icon + title
                    instead of squeezing the title into truncation */}
                <div className="flex flex-wrap items-center gap-x-12px gap-y-8px">
                    {/* flex-auto (not flex-1): keeps the natural content width as the
                        flex basis so the wrap threshold accounts for the full title */}
                    <div className="flex-auto flex items-center gap-12px min-w-0">
                        {/* HubSpot logo placeholder — orange circle with "HS" text */}
                        <div className="w-40px h-40px flex-shrink-0 rounded-lg bg-[#ff7a59] flex items-center justify-center text-white font-bold text-14px">
                            HS
                        </div>
                        {/* font-sans/normal-case/tracking-normal: card title, not a page
                            heading — opt out of the global all-caps Lulo One h3 style */}
                        <h3 className="min-w-0 truncate font-sans normal-case tracking-normal text-18px font-semibold text-gray-900">
                            HubSpot
                        </h3>
                    </div>

                    {/* Connection status badge */}
                    {status?.connected ? (
                        <span className="flex-shrink-0 inline-flex items-center gap-4px px-8px py-4px rounded-full text-12px font-medium whitespace-nowrap bg-green-100 text-green-800">
                            <span className="w-6px h-6px rounded-full bg-green-500"></span>
                            Connected
                        </span>
                    ) : (
                        <span className="flex-shrink-0 inline-flex items-center gap-4px px-8px py-4px rounded-full text-12px font-medium whitespace-nowrap bg-gray-100 text-gray-600">
                            <span className="w-6px h-6px rounded-full bg-gray-400"></span>
                            Not Connected
                        </span>
                    )}
                </div>
                {/* Full-width on mobile; from tablet up, indented to align with the title */}
                <p className="text-14px text-gray-500 mt-8px tablet:mt-4px tablet:pl-52px">
                    Sync contacts and companies with HubSpot CRM
                </p>
            </div>

            {/* Error banner */}
            {error && (
                <div className="mb-16px p-12px rounded-lg bg-red-50 border border-red-200 text-14px text-red-700">
                    {error}
                </div>
            )}

            {/* Success banner */}
            {success && (
                <div className="mb-16px p-12px rounded-lg bg-green-50 border border-green-200 text-14px text-green-700">
                    {success}
                </div>
            )}

            {status?.connected ? (
                /* ---- Connected State ---- */
                <div>
                    {/* Portal info */}
                    <div className="bg-gray-50 rounded-lg p-16px mb-16px">
                        <div className="grid grid-cols-1 tablet:grid-cols-2 gap-12px text-14px">
                            <div className="min-w-0">
                                <span className="text-gray-500">Portal</span>
                                <p className="font-medium text-gray-900 break-words">{status.portal_name || 'Unknown'}</p>
                            </div>
                            <div className="min-w-0">
                                <span className="text-gray-500">Portal ID</span>
                                <p className="font-medium text-gray-900 break-words">{status.portal_id || '—'}</p>
                            </div>
                            <div className="min-w-0">
                                <span className="text-gray-500">Connected By</span>
                                <p className="font-medium text-gray-900 break-words">{status.connected_by?.name || '—'}</p>
                            </div>
                            <div className="min-w-0">
                                <span className="text-gray-500">Connected</span>
                                <p className="font-medium text-gray-900">
                                    {status.connected_at
                                        ? new Date(status.connected_at).toLocaleDateString()
                                        : '—'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Disconnect button with confirmation */}
                    {showDisconnectConfirm ? (
                        <div className="flex flex-col tablet:flex-row tablet:items-center gap-12px">
                            <p className="text-14px text-red-600">
                                Are you sure? This will remove the HubSpot connection.
                            </p>
                            <button
                                onClick={handleDisconnect}
                                disabled={disconnecting}
                                className="px-16px py-8px text-14px font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 min-h-44px"
                            >
                                {disconnecting ? 'Disconnecting...' : 'Yes, Disconnect'}
                            </button>
                            <button
                                onClick={() => setShowDisconnectConfirm(false)}
                                className="px-16px py-8px text-14px font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 min-h-44px"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowDisconnectConfirm(true)}
                            className="px-16px py-8px text-14px font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 min-h-44px"
                        >
                            Disconnect
                        </button>
                    )}
                </div>
            ) : (
                /* ---- Disconnected State — Token Input Form ---- */
                <div>
                    <p className="text-14px text-gray-600 mb-12px">
                        Enter your HubSpot Private App access token to connect. You can create a Private App in your
                        HubSpot account under Settings &gt; Integrations &gt; Private Apps.
                    </p>

                    <div className="flex flex-col tablet:flex-row gap-12px">
                        {/* Token input — password type to prevent shoulder-surfing */}
                        <input
                            type="password"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            className="flex-1 min-w-0 px-12px py-8px border border-gray-300 rounded-lg text-16px focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent min-h-44px"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConnect();
                            }}
                        />
                        <button
                            onClick={handleConnect}
                            disabled={connecting || !token.trim()}
                            className="w-full tablet:w-auto px-20px py-8px text-14px font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 whitespace-nowrap min-h-44px"
                        >
                            {connecting ? 'Connecting...' : 'Test & Connect'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
